import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, CircularProgress,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import labels from './Labels/AddTalentModalLabel';
import CustomAlert from './CustomAlert';
import axios from 'axios';

const AjouterTalentModal = ({ open, onClose ,onSuccess, onOpenCvView  }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [files, setFiles] = useState([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [showUpdateConfirm, setShowUpdateConfirm] = useState('');
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const [alertMessages, setAlertMessages] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [generatedCVs, setGeneratedCVs] = useState({});
  const [selectedCVs, setSelectedCVs] = useState({});
  const [iaModal, setIaModal] = useState({ open: false, talent: null });
  const [autoGenLoading, setAutoGenLoading] = useState(false);

  // URLs depuis les variables d'environnement - Utiliser le proxy backend pour éviter les problèmes CORS
  const API_PARSING_URL = `${API_BASE}/api/jsoncv`; // Route proxy dans le backend (évite les problèmes CORS avec nginx)
  const API_CORRECTIONS_URL = process.env.REACT_APP_API_CORRECTIONS_URL || 'https://cooptaleo.com/corrections';

  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateTalentId, setDuplicateTalentId] = useState(null);

  const role = localStorage.getItem('userType');

  const baseRoute =   role === 'admin'
    ? '/admin/add-talent'
    : role === 'commercial'
    ? '/commercial/add-talent'
    : role === 'agent'
    ? '/agent/add-talent'
    : role === 'recruteur'
    ? '/recruteur/add-talent'
    : '';

  useEffect(() => {
    if (!open) {
      setFiles([]);
      setExistingFiles([]);
    }
  }, [open]);
  
  const handleManual = () => {
    onClose();
    navigate(baseRoute);
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
    setError('');
  };

  const handleRemoveFile = (fileName) => {
    setFiles(prevFiles => prevFiles.filter(file => file.name !== fileName));
  };

  const asArray = v => Array.isArray(v) ? v : (v ? [v] : []);
  const safeJSON = v => JSON.stringify(v ?? (Array.isArray(v) ? [] : (typeof v === 'object' ? v : '')));

  function buildFormDataFromParsed(parsed, file) {
    const form = new FormData();

    form.append("recruteurId", localStorage.getItem("userId") || "");
    form.append("origine", localStorage.getItem("userType") || "recruteur");
    form.append("devise", "EUR");
    form.append("recruteurName", localStorage.getItem("recruteurName") || "");
    form.append("userId", localStorage.getItem("userId") || "");
    form.append("note", JSON.stringify([]));

    form.append("nomCandidat", parsed.nom || "");
    form.append("prenomCandidat", parsed.prenom || "");
    form.append("email", JSON.stringify(asArray(parsed.mail)));
    form.append("phone", JSON.stringify(asArray(parsed.tel)));
    form.append("LinkedIn", parsed.linkedin || "");
    form.append("trigramme", parsed.trigramme || "");
    form.append("autresLiens", JSON.stringify(parsed.autresLiens || []));
    if (file) form.append("cvUpload", file);

    form.append("poste", (parsed.poste || "").toLowerCase());
    form.append("posteActuelIntitule", parsed.posteActuelIntitule || "");
    form.append("entrepriseActuelle", parsed.entrepriseActuelle || "");
    form.append("lieuTravailActuel", parsed.lieuTravailActuel || "");
    form.append("experienceGlobale", parsed.years_exp || "");
    form.append("informationsComplementaires", parsed.informationsComplementaires || "");
    if (parsed.dateDebutPoste) {
      const debut = new Date(parsed.dateDebutPoste);
      const diff = new Date().getFullYear() - debut.getFullYear();
      form.append("anneesPoste", String(diff));
    } else {
      form.append("anneesPoste", "");
    }
    form.append("disponibilite", "");

    const edu = typeof parsed.education === "string"
      ? parsed.education.split(",").reduce((acc, part) => {
          const [k,v] = part.split(":").map(s=>s?.trim());
          if (k) acc[k] = v || "";
          return acc;
        }, {})
      : (parsed.education || {});

    form.append("niveauEtudes", parsed.niveau_etude || "");
    form.append("titre", edu.titre || "");
    form.append("date", edu.date || "");
    form.append("etablissement", edu.établissement || edu.etablissement || "");
    form.append("experienceTalent", parsed.synthese || "");
    form.append("pointsEssentiels", safeJSON(
      typeof parsed.profil === "string" ? [parsed.profil] :
      Array.isArray(parsed.profil) ? parsed.profil : []
    ));
    form.append("competences", safeJSON(parsed.mot_cles || []));
    form.append("langues", safeJSON(
      (parsed.langues || []).map(l => ({
        langue: l.langue || l.name || "",
        niveau: l.niveau || l.level || ""
      }))
    ));
    form.append("certificats", safeJSON(
      (parsed.certifications || []).map(c => ({
        title: c.title || c.nom || "",
        date: c.date || ""
      }))
    ));

    form.append("postesRecherches", "");
    form.append("typesContrat", JSON.stringify([]));
    form.append("remunerations", JSON.stringify({}));
    form.append("secteurRecherche", "");
    form.append("secteurNonRecherche", "");
    form.append("travailSouhaite", "");
    form.append("lieuxSouhaites", JSON.stringify([]));
    form.append("lieuxNonSouhaites", JSON.stringify([]));
    form.append("etatRecherche", JSON.stringify([]));
    form.append("autresProcessus", "");

    return form;
  }
  

  const runWithLimit = (limit, items, task) => {
    let i = 0, active = 0, done = 0;
    return new Promise(resolve => {
      const next = () => {
        while (active < limit && i < items.length) {
          const item = items[i++];
          active++;
          Promise.resolve(task(item))
            .catch(err => console.warn('[auto-gen] task failed:', err))
            .finally(() => {
              active--; done++;
              if (done === items.length) resolve();
              else next();
            });
        }
      };
      next();
    });
  };

  const startBackgroundGeneration = (talents) => {
    if (!Array.isArray(talents) || talents.length === 0) return;

    setAlertSeverity("info");
    setAlertMessage(`Génération des CV en arrière-plan pour ${talents.length} talent(s)…`);
    setAlertOpen(true);
    setTimeout(() => {
      runWithLimit(2, talents, async (t) => {
        await handleGenerateCVAuto(t, { manageModal: false }); 
      }).then(() => {
        setAlertSeverity("success");
        setAlertMessage("Génération des CV terminée.");
        setAlertOpen(true);
      });
    }, 0);
  };

  const checkTalentExists = async (email) => {
    try {
      const userId = localStorage.getItem("userId");
      const response = await axios.get(`${API_BASE}/api/recruteur/get-talents`, {
        params: { recruteurId: userId },
      });
  
      const talents = response.data;

      const talent = talents.find(t => Array.isArray(t.email) && t.email.includes(email));

      return talent || null; 
    } catch (error) {
      console.error("Error checking if talent exists:", error);
      setError("Erreur de vérification des talents.");
      return null;
    }
  };
  
  
  
  const handleExtract = async () => {
    if (files.length === 0) {
      setError(labels.ajouterTalent.errorNoCV);
      return;
    }
    
    try {
      setLoading(true);
      const parsedBundle = [];
      for (const file of files) {
        const fileBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const res = reader.result;
            if (typeof res !== "string") return reject(new Error("FileReader result invalide"));
            resolve(res.split(",")[1] || "");
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
  
        const payload = { name: file.name, type: file.type, file: fileBase64 };
  
        const res = await fetch(API_PARSING_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
  
        let data = null;
        try { data = await res.json(); } catch {}
        if (!res.ok || !data || typeof data !== "object") {
          setError(labels.ajouterTalent.extractError(file.name));
          return;
        }
  
        parsedBundle.push({ parsed: data, file, fileBase64 });
      }
  
      if (files.length === 1) {
        const { parsed, file } = parsedBundle[0];
        const email = parsed.mail;
        const talent = await checkTalentExists(email);
        if (talent) {
          setDuplicateTalentId(talent._id || talent.talentId);
          setDuplicateDialogOpen(true);
          return;
        }
      
        localStorage.setItem("cvParsedData", JSON.stringify(parsed));
        localStorage.setItem("cvFileName", file.name);
        localStorage.setItem(
          "cvFileFromModal",
          JSON.stringify({
            name: file.name,
            type: file.type || "",
            base64: `data:${file.type};base64,${parsedBundle[0].fileBase64}`,
          })
        );
      
        navigate(`${baseRoute}?parsed=true`);
        return;
      }
         
  
      const successes = [];
      const duplicates = [];
      const failures = [];
  
      for (const { parsed, file } of parsedBundle) {
        try {
          const form = buildFormDataFromParsed(parsed, file);
          const resp = await fetch(`${API_BASE}/api/recruteur/add-talent`, {
            method: "POST",
            body: form,
          });
          let data = {};
          try { data = await resp.json(); } catch {}
  
          if (resp.ok && data?.talent?._id) {
            successes.push({ file: file.name, talent: data.talent });
          } else if (data?.message === "Vous avez déjà enregistré cet email." && data?.talentId) {
            duplicates.push({ file: file.name, talentId: data.talentId });
          } else {
            failures.push({ file: file.name, error: data?.message || `HTTP ${resp.status}` });
          }
        } catch (e) {
          failures.push({ file: file.name, error: e.message });
        }
      }
  
      if (successes.length) {
        setAlertMessage(`${successes.length} talent(s) ajouté(s) avec succès.`);
        setAlertSeverity("success");
        setAlertOpen(true);
  
        const createdTalents = successes.map(s => s.talent).filter(Boolean);
        onSuccess?.(createdTalents);
  
        const successNames = successes.map(s => s.file);
        setFiles(prev => prev.filter(f => !successNames.includes(f.name)));
  
        startBackgroundGeneration(createdTalents);
      }
  
      if (duplicates.length) {
        setAlertMessage(`Certains talents existent déjà (${duplicates.length}).`);
        setAlertSeverity("warning");
        setAlertOpen(true);
        setExistingFiles(duplicates.map(d => d.file));
      }
  
      if (failures.length) {
        setAlertMessage(`Des erreurs sont survenues (${failures.length}).`);
        setAlertSeverity("error");
        setAlertOpen(true);
      }
  
      const shouldClose = successes.length > 0 && duplicates.length === 0 && failures.length === 0;
      if (shouldClose) {
        setFiles([]);
        setExistingFiles([]);
        setTimeout(() => { onClose?.(); }, 3000);
      }
    } catch (err) {
      console.error("Erreur parsing:", err);
      setError(labels.ajouterTalent.apiError);
    } finally {
      setLoading(false);
    }
  };
  
  
  const handleGenerateCVAuto = async (talent, { manageModal = true } = {}) => {
    const pickFirstCv = (cv) => {
      if (!cv) return null;
      if (Array.isArray(cv)) return pickFirstCv(cv[0]);
      if (typeof cv === 'object') {
        return pickFirstCv(cv.url || cv.path || cv.file || cv.filename || cv.name);
      }
      if (typeof cv === 'string') return cv.trim() || null;
      return null;
    };
  
    const buildCvUrl = (cvPath) => {
      if (!cvPath) return null;
      if (/^https?:\/\//i.test(cvPath)) return cvPath;
      return `${API_BASE}/api/recruteur/uploads/${cvPath.replace(/^\/+/, '')}`;
    };
  
    const cvPath = pickFirstCv(talent?.cvUpload);
    const cvUrl = buildCvUrl(cvPath);
    if (!cvUrl) {
      console.warn('No CV found for talent', talent?._id);
      return;
    }
  
    if (manageModal) {
      setAutoGenLoading(true);
      setIaModal({ open: true, talent });
    }
  
    try {
      const response = await fetch(cvUrl);
      if (!response.ok) throw new Error("Impossible de récupérer le CV.");
      const cvBlob = await response.blob();
  
      const formData = new FormData();
      const safeName =
        (typeof cvPath === 'string' && cvPath.split('/').pop()) || 'cv.pdf';
      formData.append('file', cvBlob, safeName);
      formData.append('choice', 'option1');
  
      const extractionRes = await fetch(`${API_CORRECTIONS_URL}/`, {
        method: 'POST',
        body: formData,
      });

      const extractionData = await extractionRes.json();
      if (extractionData.status !== 'success') throw new Error("Erreur lors de l'extraction du CV");
  
      const corrections = extractionData.data;
  
      const saveRes = await fetch(`${API_BASE}/api/cv-corrections/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          talentId: talent._id,
          data: corrections,
          userId: localStorage.getItem('userId'),
        }),
      });

      if (!saveRes.ok) throw new Error("Erreur lors de la sauvegarde des corrections");
  
      const genRes = await fetch(`${API_CORRECTIONS_URL}/user-corrections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...corrections, choice: 'option1' }),
      });
      if (!genRes.ok) throw new Error("Erreur lors de la génération du PDF");
  
      const pdfBlob = await genRes.blob();

      const formDataPDF = new FormData();
      formDataPDF.append('file', pdfBlob, 'cv_anonymise.pdf');
      formDataPDF.append('model', 'option1');
      formDataPDF.append('talentId', talent._id);
      formDataPDF.append('name', corrections.name || '');
      formDataPDF.append('title', corrections.title || '');
      formDataPDF.append('userId', localStorage.getItem('userId'));
      formDataPDF.append('recruteurName', localStorage.getItem('recruteurName'));
  
      const uploadRes = await fetch(`${API_BASE}/api/cv-generated/save`, {
        method: 'POST',
        body: formDataPDF,
      });
      if (!uploadRes.ok) throw new Error("Erreur lors de l'upload du CV généré");
      const reload = await fetch(`${API_BASE}/api/cv-generated/list/${talent._id}`);
      const newCVs = await reload.json();
      setGeneratedCVs(prev => ({ ...prev, [talent._id]: newCVs.data }));
  
      const sortedCVs = [...(newCVs.data || [])].sort((a, b) =>
        new Date(b.generatedAt || b.date || 0) - new Date(a.generatedAt || a.date || 0)
      );
      const latestCvId = sortedCVs[0]?._id;
      setSelectedCVs(prev => ({ ...prev, [talent._id]: latestCvId }));
    } catch (err) {
      console.error('handleGenerateCVAuto error:', err);
    } finally {
      if (manageModal) {
        setIaModal({ open: false, talent: null });
        setAutoGenLoading(false);
      }
    }
  };
  
  
  
  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle><b>{labels.ajouterTalent.title}</b></DialogTitle>
      <CustomAlert
        open={alertOpen}
        message={alertMessage}
        severity={alertSeverity}
        onClose={() => setAlertOpen(false)}
        autoHideDuration={10000} 
      />
      <DialogContent>
        <Typography>{labels.ajouterTalent.instructions}</Typography>
        <Box className="modal-container">
          {files.length > 0 ? (
            <>
              <Typography className="file-name green">{labels.ajouterTalent.cvAdded}</Typography>
              {files.map((f, index) => {
                const hasError = alertMessages.includes(f.name);
                return (
                  <Box key={index} className="file-box">
                    <Typography className={`file-name ${hasError ? 'red' : 'green'}`}>
                      • {f.name}
                    </Typography>
                    <DeleteIcon
                      onClick={() => handleRemoveFile(f.name)}
                      className="hover-delete"
                    />
                  </Box>
                );
              })}
            </>
          ) : (
            <>
              <input type="file" accept=".pdf" id="upload-cv" multiple hidden onChange={handleFileChange} />
              <label htmlFor="upload-cv">
                <Button variant="outlined" component="span" startIcon={<UploadFileIcon />}>
                  {labels.ajouterTalent.uploadButton}
                </Button>
              </label>
            </>
          )}
          {error && (
            <Typography className="error-message">
              {error}
            </Typography>
          )}
        </Box>
        <Typography variant="body2">
          {labels.ajouterTalent.manualFormNote}
        </Typography>
        {alertOpen && (
          <Alert severity={alertSeverity} onClose={() => setAlertOpen(false)}>
            {alertMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleManual} disabled={loading}>
          {labels.ajouterTalent.manualButton}
        </Button>
        <Button onClick={handleExtract} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : labels.ajouterTalent.extractButton}
        </Button>
      </DialogActions>
    </Dialog>
          <Dialog open={showUpdateConfirm} onClose={() => setShowUpdateConfirm(false)}>
          <DialogTitle>CV déjà existant</DialogTitle>
          <DialogContent>
            <Typography>
              Ce talent existe déjà. Voulez-vous mettre à jour ses informations ?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowUpdateConfirm(false)} color="error">
              Non
            </Button>
            <Button
              color="primary"
              onClick={() => {
                setShowUpdateConfirm(false);
                onClose();
                navigate(`${baseRoute}?parsed=true`);
              }}
            >
              Oui, mettre à jour
            </Button>
          </DialogActions>
        </Dialog>

          <Dialog
            open={duplicateDialogOpen}
            onClose={() => setDuplicateDialogOpen(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{ style: { textAlign: 'center', padding: 20, borderRadius: 15 } }}
          >
            <DialogTitle>
              <Typography variant="h5" gutterBottom color="primary">
                 Ce talent existe déjà !
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="subtitle1">
                Vous pouvez consulter les informations ou positionner le profil directement.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(`/Recruteur/talent-detail/${duplicateTalentId}`)}
              >
                Voir le détail
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDuplicateDialogOpen(false)}
              >
                Fermer
              </Button>
            </DialogActions>
          </Dialog>

</>  
  );
};

export default AjouterTalentModal;
